import React, { useState } from 'react';

/**
 * Quick Start Guide Component
 * Interactive guide for new users
 */
const QuickStartGuide = ({ userType = 'influencer', onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const influencerSteps = [
    {
      title: 'Welcome to Engaged Manila!',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            We're excited to have you here! This quick guide will help you get started with your first campaign.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">What you'll learn:</h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>How to find and apply to campaigns</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Understanding the workflow</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Creating great content</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Getting paid</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: 'Step 1: Complete Your Profile',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Before applying to campaigns, make sure your profile is complete:
          </p>
          <div className="space-y-3">
            <div className="flex items-start bg-gray-50 p-3 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h5 className="font-semibold text-gray-900">Add Profile Photo</h5>
                <p className="text-sm text-gray-600">Use a clear, professional photo</p>
              </div>
            </div>
            <div className="flex items-start bg-gray-50 p-3 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h5 className="font-semibold text-gray-900">Link Social Media</h5>
                <p className="text-sm text-gray-600">Add your Instagram, TikTok, Facebook, and X profiles</p>
              </div>
            </div>
            <div className="flex items-start bg-gray-50 p-3 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h5 className="font-semibold text-gray-900">Write Your Bio</h5>
                <p className="text-sm text-gray-600">Tell brands about your niche and audience</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Step 2: Find & Apply to Campaigns',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Browse available campaigns and apply to ones that match your niche:
          </p>
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-4 rounded-lg border border-primary-200">
            <h5 className="font-semibold text-gray-900 mb-3">Application Tips:</h5>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span>Read campaign requirements carefully</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span>Explain why you're a good fit in your application note</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span>Check compensation and timeline before applying</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span>Apply to multiple campaigns to increase your chances</span>
              </li>
            </ul>
          </div>
          <p className="text-sm text-gray-600 italic">
            💡 Brands typically respond within 1-3 business days
          </p>
        </div>
      )
    },
    {
      title: 'Step 3: Create & Submit Content',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Once approved, you'll receive a detailed content brief with:
          </p>
          <div className="space-y-3">
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <h5 className="font-semibold text-green-900 mb-1">📋 Content Template</h5>
              <p className="text-sm text-green-700">Step-by-step guide on what to create</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <h5 className="font-semibold text-green-900 mb-1">🔗 Tracking Link</h5>
              <p className="text-sm text-green-700">Your unique link to include in content (REQUIRED for payment!)</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <h5 className="font-semibold text-green-900 mb-1">📸 Example Posts</h5>
              <p className="text-sm text-green-700">See what great content looks like</p>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Important:</strong> Always include your tracking link and disclose the partnership with #ad or #sponsored
            </p>
          </div>
        </div>
      )
    },
    {
      title: 'Step 4: Track Performance',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            After publishing your content:
          </p>
          <div className="space-y-3">
            <div className="flex items-center bg-gray-50 p-3 rounded-lg">
              <span className="text-2xl mr-3">1️⃣</span>
              <span className="text-sm text-gray-700">Submit your post URL and screenshots</span>
            </div>
            <div className="flex items-center bg-gray-50 p-3 rounded-lg">
              <span className="text-2xl mr-3">2️⃣</span>
              <span className="text-sm text-gray-700">Automated verification checks your content (instant!)</span>
            </div>
            <div className="flex items-center bg-gray-50 p-3 rounded-lg">
              <span className="text-2xl mr-3">3️⃣</span>
              <span className="text-sm text-gray-700">Brand reviews and approves (1-2 days)</span>
            </div>
            <div className="flex items-center bg-gray-50 p-3 rounded-lg">
              <span className="text-2xl mr-3">4️⃣</span>
              <span className="text-sm text-gray-700">Payment processed (3-5 days)</span>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <p className="text-lg font-semibold text-green-900 mb-1">
              Total Timeline: 10-14 days 🚀
            </p>
            <p className="text-sm text-green-700">
              From application to payment in your account!
            </p>
          </div>
        </div>
      )
    },
    {
      title: 'You\'re Ready! 🎉',
      icon: '🚀',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 text-lg">
            You now know everything you need to succeed on Engaged Manila!
          </p>
          <div className="bg-gradient-to-r from-primary to-blue-500 text-white p-6 rounded-lg text-center">
            <h4 className="text-xl font-bold mb-2">Ready to Start?</h4>
            <p className="mb-4">Browse campaigns and apply to your first one!</p>
            <button
              onClick={onClose}
              className="bg-white text-primary px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Browse Campaigns →
            </button>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <h5 className="font-semibold text-gray-900 mb-2">Need Help?</h5>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• Check out our Help Center for detailed guides</p>
              <p>• Message brands directly if you have questions</p>
              <p>• Contact support at support@engagedmanila.com</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const brandSteps = [
    {
      title: 'Welcome to Engaged Manila!',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Welcome! This guide will help you launch your first influencer campaign successfully.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">What you'll learn:</h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Creating effective campaigns</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Reviewing applications</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Managing content submissions</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Payment processing</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: 'Step 1: Create Your First Campaign',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Use our smart templates to create campaigns in minutes:
          </p>
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-4 rounded-lg border border-primary-200">
            <h5 className="font-semibold text-gray-900 mb-3">Campaign Creation Tips:</h5>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span>Choose a content template (Product Review, Unboxing, etc.)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span>Set clear compensation and timeline</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span>Define your target influencer criteria</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span>Budget includes 70% upfront + 30% after completion</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: 'Step 2: Review Applications',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Review influencer applications and select the best fits:
          </p>
          <div className="space-y-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <h5 className="font-semibold text-gray-900 mb-1">Check Their Profile</h5>
              <p className="text-sm text-gray-600">Review social media, engagement rates, and past campaigns</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <h5 className="font-semibold text-gray-900 mb-1">Verify Audience Fit</h5>
              <p className="text-sm text-gray-600">Ensure their followers match your target market</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <h5 className="font-semibold text-gray-900 mb-1">Approve Quickly</h5>
              <p className="text-sm text-gray-600">Respond within 1-3 days to keep influencers engaged</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 italic">
            💡 When you approve, influencers automatically receive their tracking link and content brief
          </p>
        </div>
      )
    },
    {
      title: 'Step 3: Review Content',
      icon: null,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Our automated system helps you review content faster:
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h5 className="font-semibold text-green-900 mb-2">Automated Verification Checks:</h5>
            <div className="grid grid-cols-2 gap-2 text-sm text-green-700">
              <div>✓ Hashtags present</div>
              <div>✓ Brand mentioned</div>
              <div>✓ Tracking link used</div>
              <div>✓ Partnership disclosed</div>
              <div>✓ Screenshots provided</div>
              <div>✓ Engagement metrics</div>
            </div>
          </div>
          <p className="text-sm text-gray-700">
            Content that passes all checks can be auto-approved, saving you time!
          </p>
        </div>
      )
    },
    {
      title: 'You\'re Ready! 🎉',
      icon: '🚀',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 text-lg">
            You're all set to launch successful influencer campaigns!
          </p>
          <div className="bg-gradient-to-r from-primary to-blue-500 text-white p-6 rounded-lg text-center">
            <h4 className="text-xl font-bold mb-2">Ready to Launch?</h4>
            <p className="mb-4">Create your first campaign now!</p>
            <button
              onClick={onClose}
              className="bg-white text-primary px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Create Campaign →
            </button>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <h5 className="font-semibold text-gray-900 mb-2">Need Help?</h5>
            <div className="space-y-2 text-sm text-gray-600">
              <p>📚 Check our Help Center for campaign best practices</p>
              <p>💬 Message influencers directly for questions</p>
              <p>📧 Contact support at support@engagedmanila.com</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const steps = userType === 'influencer' ? influencerSteps : brandSteps;
  const currentStepData = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h3 className="text-xl font-semibold text-gray-900">
                {currentStepData.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>Step {currentStep + 1} of {steps.length}</span>
              <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between rounded-b-lg">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <div className="flex space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentStep ? 'bg-primary' : index < currentStep ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 font-medium flex items-center"
            >
              Next
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 font-medium"
            >
              Get Started!
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickStartGuide;
